import { useState, useEffect } from "react";
import axios from "axios";

const AVATARS = ["🦊", "🐼", "🐶", "🐱", "🦁", "🐯", "🐨", "🐸", "🦄", "🐵", "🐰", "🐧", "🤖", "👦", "👧", "🚀"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const TXT = {
  it: {
    subtitle: "Il tuo mondo di giochi ed enigmi",
    login: "Accedi", register: "Crea account", setPw: "Proteggi il tuo profilo",
    name: "Nome giocatore", password: "Password", emailOpt: "Email (facoltativa, per recupero password)",
    age: "Età", chooseAvatar: "Scegli il tuo avatar",
    loginBtn: "Accedi", registerBtn: "Crea account", setPwBtn: "Imposta password e continua",
    toRegister: "Non hai un account? Crea account", toLogin: "Hai già un account? Accedi",
    suggest: "Suggeriscimi un nome",
    legacyMsg: "Questo profilo esisteva già. Imposta una password per proteggerlo: da ora servirà per accedere.",
    pwHint: "Almeno 4 caratteri",
  },
  en: {
    subtitle: "Your world of games and puzzles",
    login: "Log in", register: "Create account", setPw: "Protect your profile",
    name: "Player name", password: "Password", emailOpt: "Email (optional, for password recovery)",
    age: "Age", chooseAvatar: "Choose your avatar",
    loginBtn: "Log in", registerBtn: "Create account", setPwBtn: "Set password and continue",
    toRegister: "No account yet? Create one", toLogin: "Already have an account? Log in",
    suggest: "Suggest a name",
    legacyMsg: "This profile already existed. Set a password to protect it: from now on it will be required to log in.",
    pwHint: "At least 4 characters",
  },
};

export default function AuthScreen({ API, lang, setLang, onAuth }) {
  const x = TXT[lang] || TXT.it;
  const [mode, setMode] = useState("login"); // login | register | setpassword
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [age, setAge] = useState(7);
  const [avatar, setAvatar] = useState("🦊");
  const [claimToken, setClaimToken] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { setError(""); }, [mode]);

  const suggestName = async () => {
    try {
      const { data } = await axios.post(`${API}/auth/suggest-name`);
      setName(data.name);
    } catch (e) { /* ignore */ }
  };

  const doLogin = async () => {
    if (!name.trim() || !password) { setError(lang === "it" ? "Inserisci nome e password" : "Enter name and password"); return; }
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { name: name.trim(), password });
      if (data.needs_password) {
        setClaimToken(data.claim_token);
        setName(data.name);
        setAvatar(data.avatar || "🦊");
        setPassword("");
        setMode("setpassword");
      } else {
        onAuth(data.token, data.child);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const doRegister = async () => {
    if (name.trim().length < 2) { setError(lang === "it" ? "Il nome deve avere almeno 2 caratteri" : "Name needs at least 2 characters"); return; }
    if (password.length < 4) { setError(lang === "it" ? "La password deve avere almeno 4 caratteri" : "Password needs at least 4 characters"); return; }
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/register`, {
        name: name.trim(), password, age, avatar, email: email.trim() || null, language: lang,
      });
      onAuth(data.token, data.child);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const doSetPassword = async () => {
    if (password.length < 4) { setError(lang === "it" ? "La password deve avere almeno 4 caratteri" : "Password needs at least 4 characters"); return; }
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/set-password`, {
        claim_token: claimToken, password, email: email.trim() || null,
      });
      onAuth(data.token, data.child);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const doGuest = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/guest`);
      onAuth(data.token, data.child);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter") {
      if (mode === "login") doLogin();
      else if (mode === "register") doRegister();
      else doSetPassword();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 p-4 flex flex-col items-center justify-center" data-testid="auth-screen">
      <div className="text-center mb-6">
        <h1 className="text-5xl md:text-6xl font-bold text-white drop-shadow-lg" data-testid="auth-title">🎮 CelestApp 🏆</h1>
        <p className="text-lg md:text-xl text-white/90 mt-3">{x.subtitle}</p>
        <div className="mt-4 flex justify-center gap-2">
          <button onClick={() => setLang("it")} className={`px-4 py-2 rounded-full font-bold transition-colors ${lang === "it" ? "bg-white text-purple-600" : "bg-black/30 text-white hover:bg-black/40"}`} data-testid="auth-lang-it">🇮🇹 Italiano</button>
          <button onClick={() => setLang("en")} className={`px-4 py-2 rounded-full font-bold transition-colors ${lang === "en" ? "bg-white text-purple-600" : "bg-black/30 text-white hover:bg-black/40"}`} data-testid="auth-lang-en">🇬🇧 English</button>
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl w-full max-w-md" onKeyDown={onKey}>
        {mode === "login" && (
          <div className="mb-5">
            <button
              onClick={doGuest}
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-500 to-teal-500 text-white text-2xl font-bold py-4 rounded-2xl hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="auth-guest-btn"
            >
              🎮 {lang === "it" ? "Gioca subito!" : "Play now!"}
            </button>
            <p className="text-center text-xs text-gray-400 mt-2">
              {lang === "it" ? "Nessuna registrazione. Accedi solo per trovare amici e fare sfide." : "No sign-up. Log in only to find friends and challenge them."}
            </p>
            <div className="flex items-center gap-2 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-gray-400 text-sm">{lang === "it" ? "oppure" : "or"}</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          </div>
        )}
        <h2 className="text-2xl font-bold text-gray-800 mb-5 text-center">
          {mode === "login" ? `🔐 ${x.login}` : mode === "register" ? `✨ ${x.register}` : `🛡️ ${x.setPw}`}
        </h2>

        {mode === "setpassword" && (
          <p className="text-sm text-purple-700 bg-purple-50 rounded-xl p-3 mb-4" data-testid="auth-legacy-msg">{x.legacyMsg}</p>
        )}

        {/* Name */}
        {mode !== "setpassword" && (
          <>
            <label className="block text-gray-700 font-medium mb-1">{x.name}</label>
            <div className="flex gap-2 mb-3">
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 focus:outline-none"
                placeholder={x.name} data-testid="auth-name-input" autoFocus
              />
              {mode === "register" && (
                <button onClick={suggestName} type="button" className="px-3 rounded-xl bg-purple-100 text-purple-700 font-bold hover:bg-purple-200" title={x.suggest} data-testid="auth-suggest-name-btn">🎲</button>
              )}
            </div>
          </>
        )}
        {mode === "setpassword" && (
          <div className="flex items-center gap-3 mb-4 justify-center">
            <span className="text-4xl">{avatar}</span>
            <span className="text-xl font-bold text-gray-800" data-testid="auth-legacy-name">{name}</span>
          </div>
        )}

        {/* Password */}
        <label className="block text-gray-700 font-medium mb-1">{x.password}</label>
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 focus:outline-none mb-1"
          placeholder="••••••" data-testid="auth-password-input"
        />
        {mode !== "login" && <p className="text-xs text-gray-400 mb-3">{x.pwHint}</p>}

        {/* Register-only fields */}
        {mode === "register" && (
          <>
            <label className="block text-gray-700 font-medium mb-1 mt-2">{x.age}</label>
            <select value={age} onChange={(e) => setAge(parseInt(e.target.value))} className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 focus:outline-none mb-3" data-testid="auth-age-select">
              {Array.from({ length: 11 }, (_, i) => i + 5).map((a) => (
                <option key={a} value={a}>{a} {lang === "it" ? "anni" : "years"}</option>
              ))}
            </select>
            <label className="block text-gray-700 font-medium mb-2">{x.chooseAvatar}</label>
            <div className="grid grid-cols-8 gap-2 mb-3">
              {AVATARS.map((a) => (
                <button key={a} type="button" onClick={() => setAvatar(a)} className={`text-2xl p-1 rounded-lg transition-all ${avatar === a ? "bg-purple-500 scale-110" : "bg-gray-100 hover:bg-gray-200"}`} data-testid={`auth-avatar-${a}`}>{a}</button>
              ))}
            </div>
          </>
        )}

        {/* Optional email (register + setpassword) */}
        {mode !== "login" && (
          <>
            <label className="block text-gray-700 font-medium mb-1">{x.emailOpt}</label>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 focus:outline-none mb-3"
              placeholder="genitore@email.it" data-testid="auth-email-input"
            />
          </>
        )}

        {error && <p className="text-red-500 text-sm font-medium mb-3 text-center" data-testid="auth-error">{error}</p>}

        <button
          onClick={mode === "login" ? doLogin : mode === "register" ? doRegister : doSetPassword}
          disabled={loading}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xl font-bold py-3 rounded-2xl hover:opacity-90 transition-opacity disabled:opacity-50"
          data-testid="auth-submit-btn"
        >
          {loading ? "..." : mode === "login" ? x.loginBtn : mode === "register" ? x.registerBtn : x.setPwBtn}
        </button>

        {mode !== "setpassword" && (
          <button
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setPassword(""); setError(""); }}
            className="w-full text-purple-600 font-medium mt-4 hover:underline"
            data-testid="auth-toggle-mode-btn"
          >
            {mode === "login" ? x.toRegister : x.toLogin}
          </button>
        )}
      </div>
    </div>
  );
}
